import { create } from "zustand";

import { ENDPOINT_PROETHICS_SUMMATIVE } from "../constants";
import { DEFAULT_SCENARIO_ID } from "../data/scenario-registry";
import { abortPendingAudio } from "../helpers/audioAbort";
import { createSSEParser } from "../helpers/parseUIMessageStream";
import { streamProethicsPost } from "../helpers/proethicsApi";
import type {
	ArcStage,
	ProethicsScenarioId,
	SummativeResult,
	TranscriptMessage,
} from "../types";

/**
 * Module-level store (not component state) so runs survive item navigation -
 * the simulation card unmounts on Next/Prev, and this is read by three separate
 * trees: the sim card, the session's Next button, and the reflect/report step.
 *
 * Each scenario keeps its own run in `runs`; the active one is also mirrored at
 * the top level so consumers can read `transcript`/`summative`/etc directly.
 * `setScenario` snapshots the outgoing run into `runs` and restores the incoming
 * one, so navigating back shows the chat and report as the learner left them.
 *
 * Reset happens on new-session creation (useSession), not on mount.
 */
export type Run = {
	hasStarted: boolean;
	transcript: TranscriptMessage[];
	arcStage: ArcStage;
	currentHint: string | null;
	hintPending: boolean;
	isStreaming: boolean;
	threadId: string | null;
	/**
	 * Id of the message whose audio just arrived over SSE. Only this one
	 * autoplays; rehydrated bubbles (item re-mount after navigation) must not.
	 * Consumed (cleared) by the audio row on mount.
	 */
	autoplayMessageId: string | null;
	hasEnded: boolean;
	summative: Partial<SummativeResult>;
	isSubmittingSummative: boolean;
	hasSubmitted: boolean;
};

// Data needed to rebuild a completed run for read-only review. The store fills
// in the "completed" flags (hasStarted/hasEnded/hasSubmitted).
export type ReviewRunSeed = {
	transcript: TranscriptMessage[];
	summative: SummativeResult;
	threadId: string | null;
};

type State = Run & {
	scenarioId: ProethicsScenarioId;
	// Persisted runs for every scenario except the active one (which lives at the
	// top level). Snapshotted/restored by `setScenario`.
	runs: Partial<Record<ProethicsScenarioId, Run>>;
	/** Top-level, not per-run: `usePlayer` instances are independent, so this is the only global signal of what's playing. */
	playingMessageId: string | null;
	startConversation: () => void;
	endConversation: () => void;
	submitSummative: () => Promise<void>;
	setScenario: (scenarioId: ProethicsScenarioId) => void;
	hydrateReviewRuns: (
		seeds: Partial<Record<ProethicsScenarioId, ReviewRunSeed>>,
	) => void;
	reset: () => void;
	patchRun: (
		scenarioId: ProethicsScenarioId,
		patch: Partial<Run> | ((run: Run) => Partial<Run>),
	) => void;
};

const INITIAL_RUN: Run = {
	hasStarted: false,
	transcript: [],
	arcStage: "confident",
	currentHint: null,
	hintPending: false,
	isStreaming: false,
	threadId: null,
	autoplayMessageId: null,
	hasEnded: false,
	summative: {},
	isSubmittingSummative: false,
	hasSubmitted: false,
};

const INITIAL = {
	...INITIAL_RUN,
	scenarioId: DEFAULT_SCENARIO_ID as ProethicsScenarioId,
	runs: {} as Partial<Record<ProethicsScenarioId, Run>>,
	playingMessageId: null as string | null,
};

// Snapshots just the run slice out of the flat state, dropping scenarioId/runs
// and the actions, so it can be stashed in `runs`.
const pickRun = (s: Run): Run => ({
	hasStarted: s.hasStarted,
	transcript: s.transcript,
	arcStage: s.arcStage,
	currentHint: s.currentHint,
	hintPending: s.hintPending,
	isStreaming: s.isStreaming,
	threadId: s.threadId,
	autoplayMessageId: s.autoplayMessageId,
	hasEnded: s.hasEnded,
	summative: s.summative,
	isSubmittingSummative: s.isSubmittingSummative,
	hasSubmitted: s.hasSubmitted,
});

export const useProethicsFlowStore = create<State>((set, get) => {
	// Scenario-scoped so a late write (trailing audio, summative) lands on the run
	// it belongs to, not the newly active one. Updater form lets callers
	// read-modify-write a stashed run. No-op is safe: `setScenario` snapshots the
	// outgoing run into `runs` before switching, so an in-flight turn's scenario
	// is always either active or stashed.
	const patchRun = (
		scenarioId: ProethicsScenarioId,
		patch: Partial<Run> | ((run: Run) => Partial<Run>),
	) => {
		const state = get();
		if (state.scenarioId === scenarioId) {
			set(typeof patch === "function" ? patch(state) : patch);
			return;
		}
		const run = state.runs[scenarioId];
		if (!run) {
			return;
		}
		const resolved = typeof patch === "function" ? patch(run) : patch;
		set({ runs: { ...state.runs, [scenarioId]: { ...run, ...resolved } } });
	};

	return {
		...INITIAL,

		startConversation: () => set({ hasStarted: true }),

		endConversation: () => {
			abortPendingAudio();
			set({ hasEnded: true });
		},

		submitSummative: async () => {
			// Only ever submit one summative per scenario run.
			if (get().hasSubmitted || get().isSubmittingSummative) {
				return;
			}
			abortPendingAudio();
			const scenarioId = get().scenarioId;
			patchRun(scenarioId, { isSubmittingSummative: true, hasSubmitted: true });

			const transcript = get().transcript;
			const feed = createSSEParser((part) => {
				if (part.type === "data-summative-stream") {
					patchRun(scenarioId, (run) => ({
						summative: { ...run.summative, ...part.data },
					}));
				} else if (part.type === "data-final-result") {
					patchRun(scenarioId, { summative: part.data });
				}
			});

			try {
				await streamProethicsPost(
					ENDPOINT_PROETHICS_SUMMATIVE,
					{
						scenarioId,
						transcript: transcript.map(({ role, text }) => ({ role, text })),
					},
					feed,
				);
			} catch {
				patchRun(scenarioId, {
					summative: {
						criteria: [],
						feedback: "Could not load the report. Please try again.",
					},
				});
			} finally {
				patchRun(scenarioId, { isSubmittingSummative: false });
			}
		},

		setScenario: (scenarioId) => {
			// Re-entering the active scenario keeps the run live. Switching stashes the
			// current run and restores the target's (or a fresh one), so each
			// scenario's chat and report persist across navigation.
			const state = get();
			if (state.scenarioId === scenarioId) {
				return;
			}
			const runs = { ...state.runs, [state.scenarioId]: pickRun(state) };
			const nextRun = runs[scenarioId] ?? INITIAL_RUN;
			set({ ...nextRun, scenarioId, runs });
		},

		// Rebuild completed runs from persisted data for read-only review. Seeds
		// every scenario's stashed run, and the top-level run when the currently
		// active scenario is among them, so both the sim and reflect items render
		// their completed state. autoplayMessageId stays null (no autoplay).
		hydrateReviewRuns: (seeds) => {
			const state = get();
			const toRun = (seed: ReviewRunSeed): Run => ({
				...INITIAL_RUN,
				hasStarted: true,
				hasEnded: true,
				hasSubmitted: true,
				isStreaming: false,
				transcript: seed.transcript,
				summative: seed.summative,
				threadId: seed.threadId,
			});
			const runs = { ...state.runs };
			for (const [scenarioId, seed] of Object.entries(seeds)) {
				if (seed) {
					runs[scenarioId as ProethicsScenarioId] = toRun(seed);
				}
			}
			const activeSeed = seeds[state.scenarioId];
			set({ runs, ...(activeSeed ? toRun(activeSeed) : {}) });
		},

		reset: () => {
			abortPendingAudio();
			set(INITIAL);
		},

		patchRun,
	};
});
